import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Edit3, LocateFixed, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import type {
  SpreadsheetDefinedNameEntry,
  SpreadsheetDefinedNameScope,
  SpreadsheetRangeAddress,
  SpreadsheetSheetListEntry,
} from '../../protocol/spreadsheetEngineProtocol'
import {
  formatSelectionAddress,
  type SpreadsheetSelectionRange,
} from '../../spreadsheet/addressing'
import {
  buildDefinedNameManagerCommitDraft,
  buildDefinedNameManagerEditorDraft,
  buildDefinedNameManagerRows,
  buildDefinedNameSheetNameMaps,
  buildSelectedDefinedNameRangeFormula,
  filterDefinedNameManagerEntries,
  type DefinedNameManagerDraft,
  type DefinedNameManagerFilterMode,
} from '../../spreadsheet/definedNamesPresentation'
import './ExcelWorkbookChrome.css'

type ExcelDefinedNameManagerDialogProps = {
  definedNames: SpreadsheetDefinedNameEntry[]
  tableNames?: Array<{
    name: string
    range: SpreadsheetSelectionRange
  }>
  sheets: SpreadsheetSheetListEntry[]
  activeSheetId: string
  selectedRange: SpreadsheetRangeAddress
  rowCount: number
  colCount: number
  loading?: boolean
  error?: string | null
  initialMode?: 'new' | null
  onInitialModeConsumed?: () => void
  onClose: () => void
  onRefresh: () => void | Promise<void>
  onUpsert: (draft: DefinedNameManagerDraft) => void | Promise<void>
  onDelete: (name: string, scope: SpreadsheetDefinedNameScope) => void | Promise<void>
  onSelect: (entry: SpreadsheetDefinedNameEntry) => void
  onSelectTable?: (range: SpreadsheetSelectionRange) => void
}

type EditorState =
  | { mode: 'new'; entry?: null }
  | { mode: 'edit'; entry: SpreadsheetDefinedNameEntry }

export function ExcelDefinedNameManagerDialog({
  definedNames,
  tableNames = [],
  sheets,
  activeSheetId,
  selectedRange,
  rowCount,
  colCount,
  loading = false,
  error,
  initialMode,
  onInitialModeConsumed,
  onClose,
  onRefresh,
  onUpsert,
  onDelete,
  onSelect,
  onSelectTable,
}: ExcelDefinedNameManagerDialogProps) {
  const { sheetNameByEngineId, sheetIdByName } = useMemo(() => buildDefinedNameSheetNameMaps(sheets), [sheets])

  const selectedRangeFormula = useMemo(
    () => buildSelectedDefinedNameRangeFormula(selectedRange, sheetNameByEngineId),
    [selectedRange, sheetNameByEngineId],
  )
  const [filterMode, setFilterMode] = useState<DefinedNameManagerFilterMode>('all')
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [nameDraft, setNameDraft] = useState('')
  const [scopeDraft, setScopeDraft] = useState<SpreadsheetDefinedNameScope>({ kind: 'workbook' })
  const [refersToDraft, setRefersToDraft] = useState(selectedRangeFormula)
  const [commentDraft, setCommentDraft] = useState('')
  const [hiddenDraft, setHiddenDraft] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    if (!editor) setRefersToDraft(selectedRangeFormula)
  }, [editor, selectedRangeFormula])

  const beginNew = useCallback(() => {
    const draft = buildDefinedNameManagerEditorDraft({
      selectedRangeFormula,
      sheetNameByEngineId,
    })
    setEditor({ mode: 'new' })
    setNameDraft(draft.name)
    setScopeDraft(draft.scope)
    setRefersToDraft(draft.refersTo)
    setCommentDraft(draft.comment)
    setHiddenDraft(draft.hidden)
    setLocalError(null)
  }, [selectedRangeFormula, sheetNameByEngineId])

  useEffect(() => {
    if (initialMode !== 'new') return
    beginNew()
    onInitialModeConsumed?.()
  }, [beginNew, initialMode, onInitialModeConsumed])

  const beginEdit = (entry: SpreadsheetDefinedNameEntry) => {
    const draft = buildDefinedNameManagerEditorDraft({
      entry,
      selectedRangeFormula,
      sheetNameByEngineId,
    })
    setEditor({ mode: 'edit', entry })
    setNameDraft(draft.name)
    setScopeDraft(draft.scope)
    setRefersToDraft(draft.refersTo)
    setCommentDraft(draft.comment)
    setHiddenDraft(draft.hidden)
    setLocalError(null)
  }

  const filteredNames = useMemo(() => filterDefinedNameManagerEntries({
    definedNames,
    filterMode,
    activeSheetId,
  }), [activeSheetId, definedNames, filterMode])
  const rows = useMemo(() => buildDefinedNameManagerRows({
    entries: filteredNames,
    sheetNameByEngineId,
  }), [filteredNames, sheetNameByEngineId])
  const tableRows = useMemo(() => tableNames.map((table) => {
    return {
      key: `table:${table.name}`,
      name: table.name,
      value: 'Table',
      refersTo: formatSelectionAddress(table.range, { colCount, rowCount }),
      scopeLabel: 'Current Sheet',
      comment: 'Excel table',
      range: table.range,
    }
  }), [colCount, rowCount, tableNames])

  const commitEditor = async () => {
    const result = buildDefinedNameManagerCommitDraft({
      nameDraft,
      scopeDraft,
      refersToDraft,
      commentDraft,
      hiddenDraft,
      definedNames,
      editingEntry: editor?.mode === 'edit' ? editor.entry : null,
      activeSheetId,
      rowCount,
      colCount,
      sheetIdByName,
    })
    if (!result.ok) {
      setLocalError(result.error)
      return
    }
    setLocalError(null)
    try {
      await Promise.resolve(onUpsert(result.draft))
      setEditor(null)
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error))
    }
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" onMouseDown={onClose}>
      <section className="fg-excel-name-manager-dialog" role="dialog" aria-modal="true" aria-label="Name Manager" onMouseDown={(event) => event.stopPropagation()}>
        <header className="fg-excel-format-dialog-header">
          <strong>Name Manager</strong>
          <button type="button" className="fg-excel-icon-button" aria-label="Close Name Manager" title="Close Name Manager" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="fg-excel-name-manager-toolbar">
          <button type="button" className="fg-excel-title-button fg-excel-title-button-primary" title="New name" onClick={beginNew}>
            <Plus size={15} />
            <span>New</span>
          </button>
          <button type="button" className="fg-excel-icon-button" title="Refresh names from engine" aria-label="Refresh names" onClick={onRefresh}>
            <RefreshCw size={15} />
          </button>
          <label>
            <span>Filter</span>
            <select value={filterMode} onChange={(event) => setFilterMode(event.target.value as DefinedNameManagerFilterMode)} aria-label="Filter names">
              <option value="all">All</option>
              <option value="workbook">Workbook</option>
              <option value="sheet">Current Sheet</option>
              <option value="errors">Errors</option>
              <option value="hidden">Hidden</option>
            </select>
          </label>
        </div>

        {(editor || localError || error) && (
          <div className="fg-excel-name-manager-editor">
            {editor && (
              <>
                <label>
                  <span>Name</span>
                  <input value={nameDraft} aria-label="Defined name" onChange={(event) => setNameDraft(event.target.value)} data-lpignore="true" autoComplete="off" />
                </label>
                <label>
                  <span>Scope</span>
                  <select
                    value={scopeDraft.kind === 'sheet' ? scopeDraft.sheet_id : 'workbook'}
                    aria-label="Defined name scope"
                    onChange={(event) => {
                      const value = event.target.value
                      setScopeDraft(value === 'workbook' ? { kind: 'workbook' } : { kind: 'sheet', sheet_id: value })
                    }}
                  >
                    <option value="workbook">Workbook</option>
                    {sheets.map((sheet) => (
                      <option key={sheet.id} value={`sheet_${sheet.id}`}>{sheet.name}</option>
                    ))}
                  </select>
                </label>
                <label className="fg-excel-name-manager-refers-to">
                  <span>Refers To</span>
                  <input value={refersToDraft} aria-label="Defined name reference" onChange={(event) => setRefersToDraft(event.target.value)} data-lpignore="true" autoComplete="off" />
                </label>
                <button type="button" className="fg-excel-title-button" title="Use current selection" onClick={() => setRefersToDraft(selectedRangeFormula)}>
                  Selection
                </button>
                <label className="fg-excel-name-manager-comment">
                  <span>Comment</span>
                  <input value={commentDraft} aria-label="Defined name comment" onChange={(event) => setCommentDraft(event.target.value)} data-lpignore="true" autoComplete="off" />
                </label>
                <label className="fg-excel-name-manager-hidden">
                  <input type="checkbox" checked={hiddenDraft} onChange={(event) => setHiddenDraft(event.target.checked)} />
                  <span>Hidden</span>
                </label>
                <div className="fg-excel-name-manager-editor-actions">
                  <button type="button" className="fg-excel-title-button" onClick={() => setEditor(null)}>Cancel</button>
                  <button type="button" className="fg-excel-title-button fg-excel-title-button-primary" onClick={commitEditor}>
                    {editor.mode === 'edit' ? 'Save' : 'Create'}
                  </button>
                </div>
              </>
            )}
            {(localError || error) && <div className="fg-excel-name-manager-error" role="alert">{localError || error}</div>}
          </div>
        )}

        <div className="fg-excel-name-manager-table-wrap">
          <table className="fg-excel-name-manager-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Value</th>
                <th>Refers To</th>
                <th>Scope</th>
                <th>Comment</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && tableRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fg-excel-name-manager-empty">{loading ? 'Loading names...' : 'No defined names'}</td>
                </tr>
              ) : rows.map((row, index) => {
                const entry = filteredNames[index]
                return (
                  <tr key={row.key} data-has-error={row.hasError ? 'true' : 'false'}>
                    <td title={row.name}>{row.name}</td>
                    <td title={row.value}>{row.value}</td>
                    <td title={row.refersTo}>{row.refersTo}</td>
                    <td title={row.scopeLabel}>{row.scopeLabel}</td>
                    <td title={row.comment}>{row.comment}</td>
                    <td>
                      <div className="fg-excel-name-manager-actions">
                        <button type="button" className="fg-excel-icon-button" title={row.editTitle} aria-label={row.editAriaLabel} onClick={() => beginEdit(entry)}>
                          <Edit3 size={15} />
                        </button>
                        <button type="button" className="fg-excel-icon-button" title={row.selectTitle} aria-label={row.selectAriaLabel} disabled={!row.canSelect} onClick={() => onSelect(entry)}>
                          <LocateFixed size={15} />
                        </button>
                        <button type="button" className="fg-excel-icon-button" title={row.deleteTitle} aria-label={row.deleteAriaLabel} onClick={() => onDelete(entry.name, entry.scope)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              }).concat(tableRows.map((row) => (
                <tr key={row.key} data-has-error="false">
                  <td title={row.name}>{row.name}</td>
                  <td title={row.value}>{row.value}</td>
                  <td title={row.refersTo}>{row.refersTo}</td>
                  <td title={row.scopeLabel}>{row.scopeLabel}</td>
                  <td title={row.comment}>{row.comment}</td>
                  <td>
                    <div className="fg-excel-name-manager-actions">
                      <button type="button" className="fg-excel-icon-button" title={`${row.name} is table metadata; rename it from the selected table label.`} aria-label={`${row.name} is table metadata`} disabled>
                        <Edit3 size={15} />
                      </button>
                      <button type="button" className="fg-excel-icon-button" title={`Select ${row.name}`} aria-label={`Select table ${row.name}`} onClick={() => onSelectTable?.(row.range)}>
                        <LocateFixed size={15} />
                      </button>
                      <button type="button" className="fg-excel-icon-button" title={`${row.name} is table metadata; delete it from the table context menu.`} aria-label={`${row.name} is table metadata`} disabled>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
